import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Star, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const ReviewSection = ({ productId }) => {
  const { user, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(response.data.reviews);
      if (user) {
        setHasReviewed(response.data.reviews.some(r => r.user_id === user.id));
      }
    } catch (error) {
      console.error("Failed to fetch reviews");
    }
  };

  const handleSubmit = async () => {
    if (!rating) {
      toast.error("Please select a rating");
      return;
    }
    if (!comment.trim()) {
      toast.error("Please write a comment");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/products/${productId}/reviews`,
        { rating, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Review submitted!");
      setRating(0);
      setComment("");
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-12" data-testid="review-section">
      <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] mb-6">
        Customer Reviews ({reviews.length})
      </h2>

      {/* Write Review */}
      {user && !hasReviewed && (
        <div className="dashboard-card mb-8" data-testid="write-review">
          <h3 className="font-medium text-[#1A1B1A] mb-4">Write a Review</h3>
          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1"
                data-testid={`star-${star}`}
              >
                <Star
                  size={24}
                  className={`transition-colors ${
                    star <= (hoverRating || rating)
                      ? "fill-[#D4A373] text-[#D4A373]"
                      : "text-[#EAE6DF]"
                  }`}
                />
              </button>
            ))}
            <span className="ml-2 text-sm text-[#5C5D5C] self-center">
              {rating > 0 ? `${rating}/5` : "Select rating"}
            </span>
          </div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience with this product..."
            className="rounded-none border-[#EAE6DF] mb-4 min-h-[80px]"
            data-testid="review-comment"
          />
          <Button
            onClick={handleSubmit}
            disabled={submitting || !rating || !comment.trim()}
            className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none"
            data-testid="submit-review-btn"
          >
            {submitting ? "Submitting..." : "Submit Review"}
          </Button>
        </div>
      )}

      {hasReviewed && (
        <div className="bg-[#3E6656]/10 p-3 mb-6 text-sm text-[#3E6656]">
          You've already reviewed this product.
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="dashboard-card text-center py-8">
          <p className="text-[#5C5D5C]">No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.map((review, index) => (
            <div key={review.id} className="dashboard-card" data-testid={`review-${index}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#EFECE5] flex items-center justify-center">
                    <User size={18} className="text-[#5C5D5C]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1B1A]">{review.user_name}</p>
                    <p className="text-xs text-[#5C5D5C]">
                      {new Date(review.created_at).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={16}
                      className={star <= review.rating ? "fill-[#D4A373] text-[#D4A373]" : "text-[#EAE6DF]"}
                    />
                  ))}
                </div>
              </div>
              <p className="mt-3 text-[#5C5D5C] text-sm leading-relaxed">{review.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
